import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './state/store';
import { IntlProviderWrapper } from './i18n/IntlProviderWrapper';
import { ErrorBoundary } from './shared/components/ErrorBoundary';
import { usePendo } from './shared/hooks/usePendo';
import { AuthProvider } from './auth/AuthProvider';
import { RequireAuth } from './auth/guards/RequireAuth';
import { RequireAdmin } from './auth/guards/RequireAdmin';
import { AccessDeniedWatcher } from './auth/AccessDeniedWatcher';
import { PageLoader } from './shared/components/PageLoader';

const LoginPage = lazy(() => import('./features/auth/pages/LoginPage'));
const AssessmentPage = lazy(() => import('./features/assessment/pages/AssessmentPage'));
const AdminPage = lazy(() => import('./features/admin/pages/AdminPage'));
const CallbackPage = lazy(() => import('./auth/routes/CallbackPage'));
const AccessDeniedPage = lazy(() => import('./auth/routes/AccessDeniedPage'));

function AppRoutes() {
  usePendo();

  return (
    <ErrorBoundary>
      <AccessDeniedWatcher />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<CallbackPage />} />
          <Route path="/access-denied" element={<AccessDeniedPage />} />
          <Route
            path="/"
            element={
              <RequireAuth>
                <AssessmentPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin"
            element={
              <RequireAuth>
                <RequireAdmin fallback={<AccessDeniedPage />}>
                  <AdminPage />
                </RequireAdmin>
              </RequireAuth>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <Provider store={store}>
      <IntlProviderWrapper>
        <BrowserRouter basename={import.meta.env.BASE_URL}>
          <AuthProvider>
            <div className="xtm-express-root min-h-screen flex flex-col bg-background">
              <AppRoutes />
            </div>
          </AuthProvider>
        </BrowserRouter>
      </IntlProviderWrapper>
    </Provider>
  );
}
